import { slices } from "@/constants/sliceNames";
import { createAsyncThunk } from "@reduxjs/toolkit";

import axios from "../../axios/defaultAxios";
import { endpoints } from "../../axios/endpoints";
import { serializeQueryParams } from "../../utils/utils";
import { errorMessage, successMessage } from "../actions/messages.actions";

const ENDPOINT = "/scheduledExecution";

/******* Get recurrent batch predictions *********/

export const requestRecurrentBatchPredictions = createAsyncThunk(
  `${slices.recurrentBatch}/requestRecurrentBatchPredictions`,
  async (params = {}, { rejectWithValue, dispatch }) => {
    params["exclude_items"] = "true";
        try {
      const response = await axios.get(`${ENDPOINT}?${serializeQueryParams(params)}`);
      if (response.status === 200 || response.status === 201) return response.data.data;
      throw new Error(response.data?.error);
    } catch (error) {
            dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestRecurrentBatchPredictionsStatus = {
  [requestRecurrentBatchPredictions.fulfilled]: (state, action) => {
    state.loading = false;
    state.recurrentPredictionsList = action.payload.data;
    state.recordsCount = action.payload.records_count;
    state.currentPage = action.payload.current_page;
  },
  [requestRecurrentBatchPredictions.pending]: (state, action) => {
    state.loading = true;
  },
  [requestRecurrentBatchPredictions.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/********* Delete recurrent batch prediction *********/

export const requestDeleteRecurrentBatchPrediction = createAsyncThunk(
  `${slices.recurrentBatch}/requestDeleteRecurrentBatchPrediction`,
  async ({ recurrentBatchId, resolve, reject }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.delete(`${ENDPOINT}/${recurrentBatchId}`);
      if (response.status === 200 || response.status === 201) {
        dispatch(successMessage("Recurrent Batch Prediction Deleted Successfully"));
        resolve && resolve();
        return { recurrentBatchId };
      }
      throw new Error(response.data?.error);
    } catch (error) {
      reject && reject();
      dispatch(errorMessage(error.response?.data?.error || error.message));
      rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestDeleteRecurrentBatchPredictionStatus = {
  [requestDeleteRecurrentBatchPrediction.fulfilled]: (state, action) => {
    state.loading = false;
    state.recurrentPredictionsList = state.recurrentPredictionsList.filter(
      (recurrentPrediction) => recurrentPrediction.id !== action.payload.recurrentBatchId
    );
  },
  [requestDeleteRecurrentBatchPrediction.pending]: (state, action) => {
    state.loading = true;
  },
  [requestDeleteRecurrentBatchPrediction.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/********* Create recurrent batch prediction *********/

export const requestCreateRecurrentBatchPrediction = createAsyncThunk(
  `${slices.recurrentBatch}/requestCreateRecurrentBatchPrediction`,
  async (recurrentBatchData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(ENDPOINT, recurrentBatchData);

      dispatch(successMessage("Scheduled Execution Created Successfully"));
      return response.data.data;
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestCreateRecurrentBatchPredictionStatus = {
  [requestCreateRecurrentBatchPrediction.fulfilled]: (state, action) => {
    state.loading = false;
    state.item = action.payload.connector;
  },
  [requestCreateRecurrentBatchPrediction.pending]: (state, action) => {
    state.loading = true;
  },
  [requestCreateRecurrentBatchPrediction.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/********* Update recurrent batch prediction state *********/

export const requestUpdateRecurrentBatchPredictionState = createAsyncThunk(
  `${slices.recurrentBatch}/requestUpdateRecurrentBatchPredictionState`,
  async ({ recurrentBatchId, pause, resolve, reject }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`${ENDPOINT}/${recurrentBatchId}/set_pause`, { pause });

      dispatch(successMessage(`recurrent prediction ${pause ? "paused" : "started"} Sucssesfully`));
      resolve && resolve();
      return { recurrentBatchId };
    } catch (error) {
      reject && reject();
      dispatch(errorMessage(error.response?.data?.error || error.message));
      rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestUpdateRecurrentBatchPredictionStateStatus = {
  [requestUpdateRecurrentBatchPredictionState.fulfilled]: (state, action) => {
    state.loading = false;
    const toUpdateIndex = state.recurrentPredictionsList.findIndex(
      (recurrentPrediction) => recurrentPrediction._id === action.payload.recurrentBatchId
    );
    state.recurrentPredictionsList[toUpdateIndex].is_paused = !state.recurrentPredictionsList[toUpdateIndex].is_paused;
  },
  [requestUpdateRecurrentBatchPredictionState.pending]: (state, action) => {
    state.loading = true;
  },
  [requestUpdateRecurrentBatchPredictionState.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

export const requestPredictByDate = (data, id) => {
  const url = endpoints.predictByDateScheduledExec.replace(":id", id);
  return async (dispatch) => {
    try {
      const res = await axios.post(url, data);
      return res;
    } catch (error) {
      dispatch(errorMessage(error?.response?.data?.error ?? error.message));
    }
  };
};

export const requestUpdateDaysToLookBack = (data, id) => {
  const url = endpoints.daysToLookBackScheduledExec.replace(":id", id);
  return async (dispatch) => {
    try {
      const res = await axios.put(url, data);
      return res;
    } catch (error) {
      dispatch(errorMessage(error?.response?.data?.error ?? error.message));
    }
  };
};

/********* Update recurrent batch prediction *********/

export const requestUpdateRecurrentBatchPrediction = createAsyncThunk(
  `${slices.recurrentBatch}/requestUpdateRecurrentBatchPrediction`,
  async ({ recurrentBatchId, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`${ENDPOINT}/${recurrentBatchId}`, data);
      if (response.status === 200 || response.status === 201) {
        dispatch(successMessage("Recurrent Batch Prediction updated Successfully"));
        return { recurrentBatchId, data };
      }
      throw new Error(response.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestUpdateRecurrentBatchPredictionStatus = {
  [requestUpdateRecurrentBatchPrediction.fulfilled]: (state, action) => {
    state.loading = false;
    const recurrentBatchIndex = state.recurrentPredictionsList.findIndex(
      (recurrentPrediction) => recurrentPrediction?.id === action.payload?.recurrentBatchId
    );
    if (recurrentBatchIndex < 0) return;
    state.recurrentPredictionsList[recurrentBatchIndex] = {
      ...state.recurrentPredictionsList[recurrentBatchIndex],
      ...action.payload?.data,
    };
  },
  [requestUpdateRecurrentBatchPrediction.pending]: (state, action) => {
    state.loading = true;
  },
  [requestUpdateRecurrentBatchPrediction.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};
